import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TabNav, Badge, Button, BackButton, LinkButton, ListItem, Icon, Card, ModalWrapper, SectionHeader, ReservationSummary, GuestInfoSection, OptionsSection, ShoppingSection, RouteInfoSection, PaymentInfoSection, Spinner } from '../../../components/common';
import VehicleInfoSection from '../../../components/service/VehicleInfoSection';
import ReviewRegisterModal from '../../../components/service/ReviewRegisterModal';
import ShoppingRefundRequest from '../../../components/service/ShoppingRefundRequest';
import { reservationService, reviewService, pricingService } from '../../../api/services';
import { formatPrice } from '../../../utils/formatPrice';
import { reservationTabs } from '../../../constants/uiConstants';
import { useMoney } from '../../../hooks/useMoney';
import { useFxRate } from '../../../contexts';
import { makeDirectionLabels, makeOptionNameLabels, filterSelectedOptions, getBadgeServiceType } from '../../../constants/serviceLabels';
import { translateApiError } from '../../../utils/apiError';

function buildStatusMap(t) {
  return {
    pending: t('reservation.pending'),
    confirmed: t('reservation.confirmed'),
    driver_arrived: t('reservation.driver_arrived'),
    driving: t('reservation.driving'),
    drive_completed: t('reservation.drive_completed'),
    completed: t('reservation.completed'),
    cancelled: t('reservation.cancelled'),
    refunded: t('reservation.refunded'),
    no_show: t('reservation.no_show'),
  };
}

function buildServiceLabels(t) {
  return {
    airport: t('reservation.private_transfer_dash', { type: t('service.airport') }),
    golf: t('reservation.private_transfer_dash', { type: t('service.golf') }),
    ski: t('reservation.private_transfer_dash', { type: t('service.ski') }),
    hospital: t('reservation.private_transfer_dash', { type: t('service.hospital') }),
    pet: t('reservation.private_transfer_dash', { type: t('service.pet') }),
    business: t('reservation.private_transfer_dash', { type: t('service.business') }),
    tour: t('nav.private_tour'),
  };
}

// 상단 뱃지에 노출되는 카테고리 라벨 (ReservationList 와 동일 패턴)
function buildServiceBadgeLabels(t) {
  return {
    airport: t('nav.private_transfer'),
    golf: t('nav.private_transfer'),
    ski: t('nav.private_transfer'),
    hospital: t('nav.private_transfer'),
    pet: t('nav.private_transfer'),
    business: t('nav.private_transfer'),
    tour: t('nav.private_tour'),
    shopping: t('nav.private_shopping'),
  };
}

function adaptReservation(raw, t, money, lang, rate) {
  const SERVICE_LABELS = buildServiceLabels(t);
  const SERVICE_BADGE_LABELS = buildServiceBadgeLabels(t);
  const directionLabels = makeDirectionLabels(t);
  const optionNameLabels = makeOptionNameLabels(t);
  // 표시용 통화 결정: 결제는 KRW지만 비한국어 사용자에게는 USD 환산해 표시 (PM 요구사항).
  // 결제 통화가 USD면 그대로 유지 (이미 외화).
  const showUsd = lang !== 'ko' && rate;
  // direction: API는 from_airport(언더스코어), directionLabels는 from-airport(대시) → 변환 후 조회
  const routeDir = raw.route?.direction;
  const directionLabel = directionLabels[routeDir]
    || directionLabels[routeDir?.replace(/_/g, '-')]
    || directionLabels[raw.service_type]
    || '';

  // 예약 일시
  const scheduledDate = raw.route
    ? `${raw.route.reservation_date || ''} ${(raw.route.departure?.time || '').slice(0, 5)}`.trim()
    : '';

  // 전화번호 — booker_phone 이 이미 E.164 (`+82...` / `+81...`) 라
  // booker_country_code 와 합치면 "+82 +81..." 같은 잘못된 합성 문자열이 만들어져
  // formatPhone 의 KR prefix 처리가 JP 번호까지 깎아내는 회귀 발생.
  // E.164 단일 값만 전달하고, 표시는 GuestInfoSection.formatPhone 이 country code 별로 분기.
  const phone = raw.booker_phone || raw.booker_country_code || '';

  // 선택 옵션 → OptionsSection: [{ label, value, price }]
  // 옵션 추가요금: useMoney 가 비한국어일 때 자동 USD 환산 → "(+US$36)" / 한국어 → "(+50,000원)"
  const options = filterSelectedOptions(raw.selected_options || []).map((o) => ({
    label: optionNameLabels[o.code] || optionNameLabels[o.name] || o.name,
    value: o.quantity > 1 ? `${o.quantity}` : t('common.select'),
    price: Number(o.total_price) > 0 ? `(+${money(Number(o.total_price))})` : null,
  }));

  // 쇼핑 — money() 가 완성 문자열(통화 포함)을 반환하므로 t('common.won') append 제거.
  const shopping = raw.shopping ? {
    order_id: raw.shopping.order_id,
    status: raw.shopping.status,
    items: (raw.shopping.items || []).map((item) => ({
      brand: item.category,
      name: item.name,
      price: money(Number(item.price)),
      qty: item.quantity,
      image_url: item.image_url,
    })),
    total: money(Number(raw.shopping.total_price)),
  } : null;

  // 결제 정보 — method는 card_name(백엔드가 카카오페이/신용카드 등 한글 라벨 저장)
  // 결제 통화별 표시:
  //   · KRW 결제 + 한국어 사용자 → "139,000원"
  //   · KRW 결제 + 비한국어 + 환율 OK → "US$101" (표시만 환산, 실제 결제는 KRW)
  //   · USD 결제(PayPal) → "$101.50 USD" (원본 통화 보존)
  const _formatAmount = (val, currency) => {
    if (val == null) return '';
    const num = Number(val);
    const cur = (currency || 'KRW').toUpperCase();
    if (cur === 'KRW') return money(num);
    if (cur === 'USD') return `$${num.toFixed(2)} USD`;
    return `${num.toFixed(2)} ${cur}`;
  };
  // PaymentInfoSection 에 넘길 (숫자만, 통화 단위는 별도 prop):
  //   · USD 결제 → 그대로 toFixed(2) + currency='USD'
  //   · KRW 결제 + 비한국어 + 환율 → 환산 정수 + currency='USD'
  //   · KRW 결제 + 한국어 / 환율 실패 → formatPrice + currency='KRW' (현행 유지)
  const _isUsdPayment = (raw.payment?.currency || 'KRW').toUpperCase() === 'USD';
  const _displayPaymentCur = (_isUsdPayment || (raw.payment && showUsd)) ? 'USD' : 'KRW';
  let _displayTotal = '';
  if (raw.payment) {
    const _num = Number(raw.payment.amount);
    if (_isUsdPayment) {
      _displayTotal = _num.toFixed(2);
    } else if (showUsd) {
      _displayTotal = Math.round(_num / rate).toLocaleString('en-US');
    } else {
      _displayTotal = formatPrice(_num);
    }
  }
  const payment = raw.payment ? {
    date: raw.payment.paid_at?.slice(0, 16)?.replace('T', ' '),
    method: raw.payment.card_name || '',
    amount: _formatAmount(raw.payment.amount, raw.payment.currency),
    total_price: _displayTotal,
    currency: _displayPaymentCur,
  } : null;

  // 환불 정보 (취소 시 payment 내 refund 필드에서 구성)
  const refund = raw.refund || (raw.payment?.refunded_at ? {
    method: raw.payment.card_name || '',
    completed_date: raw.payment.refunded_at?.slice(0, 16)?.replace('T', ' '),
    refund_amount: _formatAmount(raw.payment.refund_amount, raw.payment.currency),
    reason: raw.payment.refund_reason || '',
    reason_note: '',
    cancel_fee: '',
    cancel_fee_note: '',
  } : null);

  // 경로
  const venueLabel = raw.service_type === 'golf' ? t('form.golf_course') : raw.service_type === 'ski' ? t('form.ski_resort') : null;
  const allStopovers = raw.route?.stopovers || [];
  // BugHerd #65: 골프/스키는 stopovers[0] 이 venue(코스/스키장)로 via 에 이미 노출되므로 제외.
  // 그 외 서비스(공항/병원/반려동물/비즈니스)는 stopovers 를 그대로 노출 — 기존엔 via 가 골프/스키 전용이라
  // 다른 서비스에서 추가한 경유지가 아예 안 보이던 회귀.
  const venueAddr = allStopovers[0]?.address || raw.venue_name || raw.venue_address;
  const stopoverList = venueLabel && allStopovers.length > 0 ? allStopovers.slice(1) : allStopovers;
  const normalizedDir = routeDir?.replace(/_/g, '-') || '';
  const isFromService = normalizedDir.startsWith('from-');
  const svcType = raw.service_type || 'airport';
  const SERVICE_ICON = { airport: 'airport', golf: 'golf', ski: 'ski', hospital: 'hospital', pet: 'pet', business: 'business', tour: 'place' };
  const arrivalLabel = isFromService ? t('common.destination_label')
    : svcType === 'airport' ? t('complete.destination_airport')
    : svcType === 'hospital' ? t('complete.destination_hospital')
    : t('common.destination_label');
  // 상세 주소 메모(detail)는 RouteInfoSection 의 detail prop 으로 분리 전달 — 표기 순서: [지번][명칭] [detail] (#9)
  const _fromAddr = raw.route?.departure?.address || '';
  const _fromMemo = raw.route?.departure?.memo || '';
  const _toAddr = raw.route?.destination?.address || '';
  const _toMemo = raw.route?.destination?.memo || '';
  const route = raw.route ? {
    direction_label: directionLabel,
    from: {
      label: t('common.departure_label'),
      address: _fromAddr,
      detail: _fromMemo,
      icon: (isFromService && svcType !== 'airport') ? (SERVICE_ICON[svcType] || 'van') : 'van',
      notes: raw.memo || '',
    },
    to: {
      label: arrivalLabel,
      address: _toAddr,
      detail: _toMemo,
    },
    via: venueLabel && venueAddr ? { label: venueLabel, address: venueAddr, icon: SERVICE_ICON[svcType] || 'place' } : undefined,
    stopovers: stopoverList,
    arrivalIcon: (svcType === 'golf' || svcType === 'ski') ? 'place' : (isFromService ? 'place' : (SERVICE_ICON[svcType] || 'place')),
  } : null;

  // 투어 예약 상품명 — BE 가 destination_id → tours.name 조회 후 tour_name 으로 전달.
  // 미전달 시(예: 과거 데이터/외부 OTA) 일반 라벨 SERVICE_LABELS.tour 로 폴백.
  const tourTitle = raw.service_type === 'tour' ? (raw.tour_name || SERVICE_LABELS.tour) : null;

  return {
    ...raw,
    title: tourTitle || raw.title || SERVICE_LABELS[raw.service_type] || t('reservation.private_transfer_dash', { type: raw.service_type }),
    service_label: raw.service_label || SERVICE_BADGE_LABELS[raw.service_type] || t('nav.private_transfer'),
    scheduled_date: scheduledDate,
    payment_date: raw.payment?.paid_at?.slice(0, 16)?.replace('T', ' '),
    customer_name: raw.booker_name || raw.customer_name,
    phone,
    email: raw.booker_email || raw.email,
    vehicle: { name: raw.vehicle_name },
    options,
    shopping,
    payment,
    refund,
    route,
  };
}

export default function ReservationDetail() {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const { rate } = useFxRate();
  const lang = (i18n.language || 'ko').split('-')[0];
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // BugHerd #193: GuestReservation 에서 이메일 인증 후 박스 클릭 시 state.guestEmail 전달.
  // 비회원이 본인 예약(회원 user_id 있는) 조회할 때 BE 가 booker_email 매칭으로 허용.
  const guestEmail = location.state?.guestEmail;
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRefundPolicy, setShowRefundPolicy] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState({});
  const [refundPolicy, setRefundPolicy] = useState([]);

  // 환율/언어 변경 시 표시 가격이 자동 재계산되도록 useMemo 로 파생.
  // (adaptReservation 안에서 money/rate 를 사용하므로, raw 가 같아도 lang/rate 변동에 반응해야 함)
  const d = useMemo(
    () => rawData ? adaptReservation(rawData, t, money, lang, rate) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawData, t, lang, rate]
  );

  useEffect(() => {
    setLoading(true);
    reservationService.getById(id, guestEmail ? { email: guestEmail } : undefined)
      .then((data) => setRawData(data))
      .catch((err) => setError(translateApiError(err, t, 'errors.generic')))
      .finally(() => setLoading(false));
  }, [id, t, guestEmail]);

  // 영수증: 결제 시 발급된 포트원 공식 매출전표(카드영수증)를 새 창으로 연다.
  // 매출전표가 없으면(무료/쿠폰100%/비포트원) 기존처럼 예약서를 인쇄한다.
  // 카드영수증: 포트원 공식 매출전표(카드영수증)를 새 창으로 연다. 없으면 안내.
  const handleCardReceipt = async () => {
    try {
      const { receipt_url } = await reservationService.getReceiptUrl(
        id, guestEmail ? { email: guestEmail } : undefined
      );
      if (receipt_url) { window.open(receipt_url, '_blank', 'noopener'); return; }
    } catch (e) { /* 조회 실패 */ }
    alert(t('reservation.no_card_receipt', { defaultValue: '카드 영수증을 찾을 수 없습니다.' }));
  };

  useEffect(() => {
    pricingService.getVehicleInfo(d?.service_type || 'airport')
      .then((info) => setVehicleInfo(info))
      .catch((e) => console.warn(e.message));
    pricingService.getRefundPolicy()
      .then((policy) => setRefundPolicy(policy))
      .catch((e) => console.warn(e.message));
  }, [d?.service_type]);

  /* Scroll lock for cancel confirmation modal */
  useEffect(() => {
    if (!showCancelConfirm) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [showCancelConfirm]);

  const handleCancel = () => {
    reservationService.cancel(id)
      .then(() => {
        setShowCancelConfirm(false);
        setActiveTab('refunded');
        setRawData((prev) => ({ ...prev, status: 'cancelled' }));
      })
      .catch((err) => { setShowCancelConfirm(false); alert(err.message); });
  };

  const handleReviewSubmit = (reviewData) => {
    if (!reviewData.content?.trim()) { alert(t('common.review_write_placeholder')); return; }
    if (!reviewData.rating || reviewData.rating < 1) { alert(t('common.review_rating')); return; }
    // 후기 작성은 reservation_id (정수 PK) 기반. URL 의 id 가 reservation_number 인 케이스를 막기 위해
    // 우선 로드한 reservation 객체의 id 를 사용. d 가 없으면 URL id fallback.
    const reservationId = d?.id ?? id;
    reviewService.create({ reservation_id: reservationId, ...reviewData })
      .then(() => {
        setShowReviewModal(false);
        alert(t('reservation.review_submitted'));
      })
      .catch((err) => alert(typeof err.message === 'string' ? err.message : t('reservation.review_submit_failed')));
  };

  if (loading) return <div className="bg-mono-50 min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>;
  if (error) return <div className="bg-mono-50 min-h-screen"><p className="text-sm text-red-500 text-center py-8">{error}</p></div>;
  if (!d) return null;

  const serviceType = d.service_type || 'airport';
  const v = vehicleInfo;
  const status = buildStatusMap(t)[d.status] || d.status;
  const isRefunded = d.status === 'cancelled';
  const isCompleted = d.status === 'completed';
  const isPending = d.status === 'pending';

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* Tabs */}
      <div className="no-print">
        <TabNav
          variant="fullwidth"
          tabs={reservationTabs}
          activeTab={activeTab}
          onTabChange={(tabId) => navigate('/reservation', { state: { tab: tabId } })}
        />
      </div>

      <div className="max-w-content mx-auto px-2.5 sm:px-0 py-6 sm:py-8 space-y-4">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between gap-3">
          <BackButton />
          <div className="flex items-center gap-3">
            <Button level="level3" size="sm" onClick={() => window.print()}>{t('reservation.booking_confirmation', { defaultValue: '예약확인서' })}</Button>
            {rawData?.payment?.pg_provider === 'portone' && (
              <Button level="level3" size="sm" onClick={handleCardReceipt}>{t('reservation.receipt')}</Button>
            )}
            {!isRefunded && !isCompleted && (
              <Button level="level3" size="sm" onClick={() => setShowCancelConfirm(true)}>{t('reservation.cancel_reservation')}</Button>
            )}
            {isPending && (
              <Button
                level="level1"
                size="sm"
                onClick={() => navigate(`/transfer/${serviceType}/payment`, { state: { reservationId: d.id } })}
              >
                {t('reservation.pay_now')}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {/* === Top Info Card === */}
          <Card>
            <ReservationSummary
              service={d.service_label || t('nav.private_transfer')}
              serviceType={getBadgeServiceType(d.service_type)}
              status={status}
              paymentDate={d.payment_date}
              title={d.title}
              reservationNo={d.reservation_number}
              scheduledDate={d.scheduled_date}
            />
          </Card>

          {/* === 차량 정보 === */}
          <Card>
            <SectionHeader variant="card" title={t('complete.vehicle_info')} />
            <VehicleInfoSection
              images={v.images}
              carName={d.vehicle?.name || v.carName}
              usageTimeBadge={v.usageTimeBadge}
              specs={[
                { icon: 'van', text: v.seat },
                { icon: 'person', text: v.capacity },
                { icon: 'bag', text: v.luggage },
              ]}
              waitInfo={v.waitInfo}
              showReview={false}
              showPriceNote={false}
            />
          </Card>

          {/* === 예약자 정보 === */}
          <Card>
            <GuestInfoSection
              name={d.guest?.name || d.customer_name}
              phone={d.guest?.phone || d.phone}
              email={d.guest?.email || d.email}
            />
          </Card>

          {/* === 선택 옵션 === */}
          {d.options && d.options.length > 0 && (
            <Card>
              <OptionsSection options={d.options} />
            </Card>
          )}

          {/* === 프라이빗 쇼핑 서비스 === */}
          {d.shopping && d.shopping.items && d.shopping.items.length > 0 && (
            <Card>
              <ShoppingSection items={d.shopping.items} total={d.shopping.total} />
              {d.shopping.order_id && <ShoppingRefundRequest orderId={d.shopping.order_id} guestEmail={guestEmail} />}
            </Card>
          )}

          {/* === 이동 정보 === */}
          {d.route && (
            <Card>
              <RouteInfoSection
                directionLabel={d.route.direction_label}
                from={d.route.from}
                to={d.route.to}
                via={d.route.via}
                stopovers={d.route.stopovers || []}
                arrivalIcon={d.route.arrivalIcon || 'place'}
              />
            </Card>
          )}

          {/* === 결제 정보 === */}
          {d.payment && (
            <Card>
              <PaymentInfoSection
                date={d.payment.date}
                method={d.payment.method}
                amount={d.payment.amount}
                extraItems={d.shopping?.items?.length > 0 ? [{ label: t('payment.shopping_info'), value: d.shopping.total }] : undefined}
                totalAmount={d.payment.total_price}
                currency={d.payment.currency}
              />
            </Card>
          )}

          {/* === 추가 결제 내역 (이용 완료 only) === */}
          {isCompleted && d.additional_charges && d.additional_charges.length > 0 && (
            <Card>
              <SectionHeader variant="card" title={t('reservation.extra_charge_payment')} />
              {d.additional_charges.map((charge, i) => (
                <ListItem
                  key={i}
                  label={charge.label}
                  value={typeof charge.amount === 'number' ? money(charge.amount) : charge.amount}
                  layout="inline"
                />
              ))}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-mono-200">
                <span className="text-base font-bold text-mono-900">{t('reservation.surcharge_amount')}</span>
                <p className="flex items-baseline gap-0.5">
                  <span className="text-2xl font-bold text-mono-900">{money(Number(d.additional_total) || 0)}</span>
                </p>
              </div>
              <div className="mt-6">
                <Button level="outline-primary" fullWidth onClick={() => setShowReviewModal(true)}>{t('reservation.register_review')}</Button>
              </div>
            </Card>
          )}

          {/* === 환불 정보 (환불 완료 only) === */}
          {isRefunded && d.refund && (
            <Card>
              <SectionHeader variant="card" title={t('reservation.refunded')} />
              <div className="flex flex-col">
                <div className="flex items-start py-1.5">
                  <span className="text-2sm font-medium text-mono-500 w-[80px] sm:w-[120px] shrink-0">{t('reservation.refund_reason')}</span>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm font-medium text-mono-900 break-keep break-words">{d.refund.reason}</span>
                    <span className="text-2sm text-mono-500 break-keep break-words">{d.refund.reason_note}</span>
                  </div>
                </div>
                <ListItem label={t('reservation.refund_method')} value={d.refund.method} layout="inline" />
                <ListItem label={t('reservation.refund_date')} value={d.refund.completed_date} layout="inline" />
                <div className="flex items-start py-1.5">
                  <span className="text-2sm font-medium text-mono-500 w-[80px] sm:w-[120px] shrink-0">{t('footer.refund')}</span>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-mono-900">{d.refund.cancel_fee}</span>
                    <span className="text-2sm text-mono-500">{d.refund.cancel_fee_note}</span>
                  </div>
                </div>
                <ListItem label={t('reservation.refund_amount')} value={d.refund.refund_amount} layout="inline" />
              </div>
              <div className="flex justify-end mt-4">
                <LinkButton onClick={() => setShowRefundPolicy(true)}>{t('footer.refund')}</LinkButton>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Review Register Modal */}
      {showReviewModal && (
        <ReviewRegisterModal
          reservation={{
            service: d.service_label || t('nav.private_transfer'),
            title: d.title,
            reservationNo: d.reservation_number,
            usageDate: d.scheduled_date,
            vehicle: d.vehicle?.name,
          }}
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
        />
      )}

      {/* Refund Policy Modal */}
      {showRefundPolicy && (
        <ModalWrapper onClose={() => setShowRefundPolicy(false)} padding="p-5 sm:p-8" gap="gap-5">
          <h3 className="text-lg font-bold text-mono-900 text-center">{t('footer.refund')}</h3>
          <div className="space-y-3">
            {refundPolicy.length > 0 ? refundPolicy.map((r, i) => (
              <ListItem key={i} label={r.label} value={r.value} layout="inline" />
            )) : (
              <p className="text-sm text-mono-400 text-center py-4">{t('common.loading')}</p>
            )}
          </div>
          <Button level="level1" fullWidth onClick={() => setShowRefundPolicy(false)}>{t('common.confirm')}</Button>
        </ModalWrapper>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 space-y-4">
            <h3 className="text-lg font-bold text-mono-900 text-center">{t('reservation.cancel_modal_title')}</h3>
            <p className="text-sm text-mono-500 text-center break-keep">{t('reservation.cancel_modal_desc')}</p>
            <div className="flex gap-3">
              <Button level="level3" fullWidth onClick={() => setShowCancelConfirm(false)}>{t('common.no')}</Button>
              <Button level="level1" fullWidth onClick={handleCancel}>{t('common.yes')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
